import { CircleAlert, Info, TriangleAlert } from 'lucide-react';
import { createElement } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import type { DashboardAlert, DashboardAlertTone } from '../dashboard.api';

interface AlertPanelProps {
  alerts: DashboardAlert[] | undefined;
  loading: boolean;
}

const TONES: Record<DashboardAlertTone, { wrapper: string; icon: string }> = {
  danger: { wrapper: 'border-rose-200 bg-rose-50 text-rose-800', icon: 'text-rose-500' },
  warning: { wrapper: 'border-amber-200 bg-amber-50 text-amber-900', icon: 'text-amber-500' },
  info: { wrapper: 'border-brand-200 bg-brand-50 text-brand-900', icon: 'text-brand-500' },
};

const ICONS: Record<DashboardAlertTone, typeof CircleAlert> = {
  danger: TriangleAlert,
  warning: TriangleAlert,
  info: Info,
};

/** Alertas operativas: tanques en nivel crítico/bajo y comprobantes rechazados. */
export function AlertPanel({ alerts, loading }: AlertPanelProps) {
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-14 rounded-control" />
        <Skeleton className="h-14 rounded-control" />
      </div>
    );
  }

  const lista = alerts ?? [];

  if (lista.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-control border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
        <Info className="size-4.5 shrink-0 text-emerald-500" aria-hidden="true" />
        <p>Sin alertas activas: todos los tanques están sobre el mínimo y no hay comprobantes rechazados.</p>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {lista.map((alerta, index) => {
        const tone = TONES[alerta.tipo] ?? TONES.info;

        return (
          <li
            key={`${alerta.titulo}-${index}`}
            className={cn('flex items-start gap-3 rounded-control border px-4 py-3 text-sm', tone.wrapper)}
          >
            {createElement(ICONS[alerta.tipo] ?? ICONS.info, {
              className: cn('mt-px size-4.5 shrink-0', tone.icon),
              'aria-hidden': true,
            })}

            <div className="min-w-0">
              <p className="font-semibold">{alerta.titulo}</p>
              <p className="mt-0.5 leading-relaxed opacity-90">{alerta.mensaje}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
